import { types } from "mobx-state-tree";
import { logger } from "@fidget/common/utils";
import { inboxStoreGetter } from "../utils";
import {
  ConcreteGlobalFilters,
  createGlobalFilterOfType,
  FilterTags,
  GlobalFilterTypes,
  IFilterTags,
  FilterAssignee,
  FilterAnyThreadClosed,
  FilterTagGroup,
  IFilterTagGroup
} from "./ConcreteGlobalFilters";
import { IInboxEntry } from "./InboxEntry";
import { ITagGroup } from "../tagGroups/TagGroupStore";

export const GlobalFilter = types
  .model("GlobalFilter", {
    activeFilters: types.array(ConcreteGlobalFilters)
  })
  .views(inboxStoreGetter)
  .views((self) => {
    const activeFiltersChecks = () => {
      return self.activeFilters.map((filter) => filter.doesEntryBelong);
    };

    const views = {
      get activeTagsFilter(): IFilterTags | undefined {
        const maybeFilter = self.activeFilters.find(
          (filter) => filter.type === GlobalFilterTypes.Tags
        );
        return maybeFilter ? (maybeFilter as IFilterTags) : undefined;
      },

      get activeTagGroupsFilter(): IFilterTagGroup | undefined {
        const maybeFilter = self.activeFilters.find(
          (filter) => filter.type === GlobalFilterTypes.TagGroup
        );
        return maybeFilter ? (maybeFilter as IFilterTagGroup) : undefined;
      },

      getFilteredEntries(entries: Array<IInboxEntry>): Array<IInboxEntry> {
        const filterChecks = activeFiltersChecks();

        return self.activeFilters.length
          ? entries.filter((entry) =>
            filterChecks.every((check) => check(entry, self.inboxStore.selectedFilter))
          )
          : entries;
      },

      isFilterEnabled(type: GlobalFilterTypes): boolean {
        return !!self.activeFilters.find((filter) => filter.type === type);
      },

      getFilter(type: GlobalFilterTypes) {
        return self.activeFilters.find((filter) => filter.type === type);
      },

      get isAnyFilterEnabled(): boolean {
        return self.activeFilters.length > 0;
      },

      get activeTags(): Array<string> | undefined {
        return views.activeTagsFilter?.tags;
      },

      get activeTagGroupIds(): Array<string> | undefined {
        return views.activeTagGroupsFilter?.tagGroups.map(tg => tg.id);
      }
    };

    return views;
  })
  .actions((self) => {
    const onFiltersChanged = (forceRefresh = false) => {
      self.inboxStore.filters.forEach((filter) => filter.handleGlobalFilterChanged(forceRefresh));
    };

    const activateFilterWithoutCheckEnabled = (type: GlobalFilterTypes) => {
      if (type === GlobalFilterTypes.Tags) {
        logger.warn("Incorrect use of activateFilter, use activateTagsFilter for tag filtering");
        return;
      }

      if (type === GlobalFilterTypes.YoursNew) {
        actions.deactivateFilter(GlobalFilterTypes.YoursUnsnoozed);
        actions.deactivateFilter(GlobalFilterTypes.Snoozed);
      } else if (type === GlobalFilterTypes.YoursUnsnoozed) {
        actions.deactivateFilter(GlobalFilterTypes.Snoozed);
        actions.deactivateFilter(GlobalFilterTypes.YoursNew);
      } else if (type === GlobalFilterTypes.Snoozed) {
        actions.deactivateFilter(GlobalFilterTypes.YoursUnsnoozed);
        actions.deactivateFilter(GlobalFilterTypes.YoursNew);
      }

      self.activeFilters.push(createGlobalFilterOfType(type));
      onFiltersChanged();
    };

    const actions = {
      activateTagsFilter(tags: Array<string>) {
        self.activeFilters.replace(
          self.activeFilters.filter((filter) => filter.type !== GlobalFilterTypes.Tags)
        );
        self.activeFilters.push(FilterTags.create({ tags }));
        onFiltersChanged();
      },

      activateTagGroupFilter(tagGroups: Array<ITagGroup>) {
        self.activeFilters.replace(
          self.activeFilters.filter((filter) => filter.type !== GlobalFilterTypes.TagGroup)
        );
        self.activeFilters.push(FilterTagGroup.create({ tagGroups }));
        onFiltersChanged();
      },

      activateAssigneeFilter(assigneeId: string) {
        self.activeFilters.replace(
          self.activeFilters.filter((filter) => filter.type !== GlobalFilterTypes.Assignee)
        );
        self.activeFilters.push(FilterAssignee.create({ assigneeId }));
        onFiltersChanged();
      },

      activateAnyThreadClosedFilter(after: number, before: number) {
        self.activeFilters.replace(
          self.activeFilters.filter((filter) => filter.type !== GlobalFilterTypes.AnyThreadClosedAt)
        );
        self.activeFilters.push(FilterAnyThreadClosed.create({ after, before }));
        onFiltersChanged(true);
      },

      activateFilter(type: GlobalFilterTypes) {
        if (!self.isFilterEnabled(type)) {
          activateFilterWithoutCheckEnabled(type);
        }
      },

      deactivateFilter(type: GlobalFilterTypes) {
        self.activeFilters.replace(self.activeFilters.filter((filter) => filter.type !== type));
        onFiltersChanged(type === GlobalFilterTypes.AnyThreadClosedAt);
      },

      toggleFilter(type: GlobalFilterTypes) {
        const isEnabled = self.isFilterEnabled(type);

        if (isEnabled) {
          actions.deactivateFilter(type);
        } else {
          activateFilterWithoutCheckEnabled(type);
        }
      },

      deactivateAll() {
        self.activeFilters.clear();
        onFiltersChanged();
      }
    };

    return actions;
  });
